import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Pagination,
  Chip,
} from '@mui/material';
import { Refresh, Search } from '@mui/icons-material';
import { eventLogsAPI } from '../services/api';

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
};

const PAGE_SIZE = 50;

export default function EventLogsPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // 필터
  const [orderNumber, setOrderNumber] = useState('');
  const [eventType, setEventType] = useState('');
  const [eventTypes, setEventTypes] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await eventLogsAPI.getAll({
        order_number: orderNumber || undefined,
        event_type: eventType || undefined,
        page,
        limit: PAGE_SIZE,
      });
      setLogs(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('이벤트 로그 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [orderNumber, eventType, page]);

  const loadEventTypes = useCallback(async () => {
    try {
      const res = await eventLogsAPI.getEventTypes();
      setEventTypes(res.data.data || []);
    } catch (err) {
      // ignore
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadEventTypes(); }, [loadEventTypes]);

  const handleSearch = () => {
    setPage(1);
    load();
  };

  const handleReset = () => {
    setOrderNumber('');
    setEventType('');
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">이벤트 로그</Typography>
        <Button startIcon={<Refresh />} onClick={load} variant="outlined">새로고침</Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            label="거래번호"
            size="small"
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: 220 }}
          />
          <TextField
            select
            label="작업 유형"
            size="small"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">전체</MenuItem>
            {eventTypes.map(t => (
              <MenuItem key={t.event_type} value={t.event_type}>
                {t.event_name} ({t.cnt})
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" startIcon={<Search />} onClick={handleSearch}>검색</Button>
          <Button variant="text" onClick={handleReset}>초기화</Button>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary">총 {total}건</Typography>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold', width: 200 }}>거래번호</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 180 }}>일시</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>작업명</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 120 }}>직원 이름</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                  이벤트 로그가 없습니다.
                </TableCell>
              </TableRow>
            ) : logs.map(log => (
              <TableRow key={log.log_id} hover>
                <TableCell>
                  {log.order_number ? (
                    <Chip label={log.order_number} size="small" variant="outlined" />
                  ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  )}
                </TableCell>
                <TableCell>{formatDateTime(log.created_at)}</TableCell>
                <TableCell>{log.event_name}</TableCell>
                <TableCell>{log.staff_name || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            color="primary"
          />
        </Box>
      )}
    </Container>
  );
}
